import React, { useEffect, useMemo, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import { ScrollArea } from '@rocco/components/scroll-area';
import type { Message } from '../../types';
import { groupMessagesByDate } from '../../utils';
import { ChatDateSeparator } from '../messages/ChatDateSeparator';
import { ChatMessageBubble } from '../messages/ChatMessageBubble';

/**
 * Preview component for showing which messages will be migrated when
 * inviting users to a 1-on-1 chat. Displays messages in ascending order
 * (oldest first) with scroll down to load more newer messages.
 */
export function MessagesMigrationPreview({
    className,
    messages,
    placeholderText,
    scrollUpCounter = 0,
    isLoading,
    isEmpty,
    hasMore,
    onLoadMore,
}: {
    className?: string;
    /** Messages in ascending order (oldest first). */
    messages: Message[];
    /** The text to display when the messages list is empty. */
    placeholderText?: string;
    /** Update this variable to trigger scroll up to the top. */
    scrollUpCounter?: number;
    /** The hook is currently fetching messages. */
    isLoading: boolean;
    /** The messages list is empty after fetching. */
    isEmpty: boolean;
    /** There are more (newer) messages to load. */
    hasMore: boolean;
    /** Callback to load more newer messages. */
    onLoadMore?: () => void;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
    const groupedMessages = useMemo(() => {
        // Reverse to descending order for the grouping function.
        const messagesDesc = [...messages].reverse();
        return groupMessagesByDate(messagesDesc);
    }, [messages]);

    // Scroll to the top when the counter changes, e.g. the fromTimestamp changes.
    useEffect(() => {
        containerRef.current?.scrollTo({
            top: 0,
            behavior: 'instant',
        });
    }, [scrollUpCounter]);

    // Trigger load more messages when the user scrolls to the bottom.
    useEffect(() => {
        const sentinel = loadMoreSentinelRef.current;
        const scrollContainer = containerRef.current;
        if (!sentinel || !scrollContainer || !hasMore || isLoading) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (
                    onLoadMore &&
                    entry?.isIntersecting &&
                    hasMore &&
                    !isLoading
                ) {
                    onLoadMore();
                }
            },
            { root: scrollContainer },
        );
        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, isLoading, onLoadMore]);

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <p className="text-[12px] leading-tight font-medium text-black/70">
                Preview what's included
            </p>
            <div className="h-[200px] w-full rounded-md border border-gray-200 bg-[#f2f2f2]">
                {!isEmpty || isLoading ? (
                    <ScrollArea
                        ref={containerRef}
                        className={cn(
                            'size-full',
                            // Fix for width overflow issue.
                            // https://github.com/radix-ui/primitives/issues/2722
                            '[&>div>div]:block!',
                        )}
                        scrollBarClassName="w-[8px]"
                        thumbClassName="bg-gray-500"
                        overscroll="contain"
                    >
                        <div
                            className="relative flex flex-col gap-[4px] p-[8px] select-text"
                            role="log"
                            aria-live="polite"
                            aria-label="Messages to be migrated"
                        >
                            {hasMore && (
                                <div
                                    ref={loadMoreSentinelRef}
                                    className="invisible absolute inset-x-0 bottom-[100px] h-px"
                                    aria-hidden="true"
                                ></div>
                            )}
                            {groupedMessages.map(
                                ({ date, a11yString, messages }) => (
                                    <React.Fragment key={a11yString}>
                                        <ChatDateSeparator
                                            className="mx-auto my-[4px] min-w-[25%]"
                                            date={date}
                                        />
                                        {messages.map((message, index) => {
                                            const previousMessage =
                                                index > 0
                                                    ? messages[index - 1]
                                                    : undefined;
                                            const previousSender =
                                                previousMessage?.Participant;
                                            return (
                                                <ChatMessageBubble
                                                    key={message.ID}
                                                    message={message}
                                                    hideSender={
                                                        previousMessage &&
                                                        previousSender &&
                                                        message.Participant
                                                            .ID ===
                                                            previousSender.ID &&
                                                        previousMessage.__typename !==
                                                            'SystemMessage' &&
                                                        previousMessage.__typename !==
                                                            'RevealContactDetailsMessage'
                                                    }
                                                    hideActions
                                                />
                                            );
                                        })}
                                    </React.Fragment>
                                ),
                            )}
                        </div>
                    </ScrollArea>
                ) : (
                    <div className="flex size-full items-center justify-center p-[16px]">
                        <p className="text-center text-[12px] leading-normal text-black/70">
                            {placeholderText ||
                                "Since there aren't any messages from this time, no conversation history will be included."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
